import React, { useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DailyArcadeChallenge = ({ onPlayGame, completed = false }) => {
    const { t } = useTranslation();

    // Deterministic daily challenge based on date
    const challenge = useMemo(() => {
        const today = new Date();
        const dayIndex = today.getDate() % 5;
        
        const games = [
            { id: 'auraEater', name: 'Aura Eater', target: 500, icon: 'Flame', color: 'text-orange-400', bg: 'from-orange-500/20 to-red-500/20' },
            { id: 'synapticStacking', name: 'Synaptic Stacking', target: 200, icon: 'Layers', color: 'text-blue-400', bg: 'from-blue-500/20 to-cyan-500/20' },
            { id: 'synapticSerpent', name: 'Synaptic Serpent', target: 50, icon: 'Spline', color: 'text-green-400', bg: 'from-green-500/20 to-emerald-500/20' },
            { id: 'cosmicAscent', name: 'Cosmic Ascent', target: 300, icon: 'Rocket', color: 'text-purple-400', bg: 'from-purple-500/20 to-pink-500/20' },
            { id: 'starlightSentinel', name: 'Starlight Sentinel', target: 400, icon: 'Shield', color: 'text-yellow-400', bg: 'from-yellow-500/20 to-amber-500/20' }
        ];

        return games[dayIndex];
    }, []);

    const Icon = LucideIcons[challenge.icon];

    return (
        <div className={`relative overflow-hidden rounded-2xl border border-white/10 p-6 mb-8 bg-gradient-to-r ${challenge.bg} shadow-lg transition-all duration-300 hover:shadow-cyan-500/10`}>
            <div className="absolute top-0 right-0 p-4 opacity-20">
                <Icon size={120} />
            </div>
            
            <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                    <div className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white border border-white/20">
                        {t('dailyChallenge.badge', 'Daily Training Protocol')}
                    </div>
                    {completed && (
                        <div className="bg-green-500/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-green-300 border border-green-500/30 flex items-center gap-1">
                            <LucideIcons.Check size={12} /> {t('dailyChallenge.completed', 'Training Complete')}
                        </div>
                    )}
                </div>

                <h3 className="text-3xl font-bold text-white font-orbitron mb-1">{challenge.name}</h3>
                <p className="text-gray-300 mb-6 max-w-md">
                    {t('dailyChallenge.instruction', { target: challenge.target, game: challenge.name, defaultValue: `Complete training module: Score over ${challenge.target} in ${challenge.name}` })}
                </p>

                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => onPlayGame(challenge.id)}
                        className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all transform hover:scale-105 active:scale-95 shadow-lg ${challenge.color.replace('text-', 'bg-').replace('-400', '-600')} text-white hover:brightness-110`}
                    >
                        <LucideIcons.Gamepad2 size={20} />
                        {t('dailyChallenge.playNow', 'Initiate Training')}
                    </button>
                    
                    <div className="text-sm font-medium text-gray-400">
                        {t('dailyChallenge.reward', 'Reward:')} <span className="text-white font-bold">50 {t('nexusDreamWeave.souls')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DailyArcadeChallenge;
